import React from 'react';
import { useScheduleEventResultsQuery } from 'features/events/queries';

const FOOTBALL_SPORT_ID = 50;
const BASEBALL_SPORT_ID = 40;

const MAX_POLL_FAILURES = 5;

/**
 * During the live window, ongoingPayload (GET /live) is the single source of truth.
 * The event results query is only enabled after the live window ends (for results display).
 */
export function useEventResultsFetcher(params: {
  scheduleId: string | number | null | undefined;
  liveEventId: string | number | null | undefined;
  scheduleJustChanged: boolean;
  inLiveWindowByTime: boolean;
  countdownFinished: boolean;
  sportId: string | number | null;
  hasOngoingCurrent: boolean;
  ongoingPayload: Record<string, unknown> | null;
}) {
  const {
    scheduleId,
    liveEventId,
    scheduleJustChanged,
    inLiveWindowByTime,
    countdownFinished,
    sportId,
    hasOngoingCurrent,
    ongoingPayload,
  } = params;

  const pollFailureCountRef = React.useRef(0);
  const [pollExhausted, setPollExhausted] = React.useState(false);

  React.useEffect(() => {
    pollFailureCountRef.current = 0;
    setPollExhausted(false);
  }, [liveEventId, scheduleId]);

  const eventResultsQuery = useScheduleEventResultsQuery({
    scheduleId: scheduleId ?? undefined,
    eventId: liveEventId ?? undefined,
    enabled: Boolean(
      !scheduleJustChanged &&
        countdownFinished &&
        (!inLiveWindowByTime || !hasOngoingCurrent) &&
        liveEventId &&
        scheduleId
    ),
    refetchInterval: (inLiveWindowByTime && !hasOngoingCurrent && !pollExhausted) ? 5_000 : false,
  });

  React.useEffect(() => {
    if (eventResultsQuery.isError) {
      pollFailureCountRef.current += 1;
      if (pollFailureCountRef.current >= MAX_POLL_FAILURES) {
        setPollExhausted(true);
      }
    } else if (eventResultsQuery.isSuccess && eventResultsQuery.data && Object.keys(eventResultsQuery.data).length > 0) {
      pollFailureCountRef.current = 0;
      setPollExhausted(false);
    }
  }, [eventResultsQuery.isError, eventResultsQuery.isSuccess, eventResultsQuery.data, eventResultsQuery.dataUpdatedAt, eventResultsQuery.errorUpdatedAt]);

  // During the live window, use ongoingPayload directly. After live, use event results query data.
  const effectiveEventResults: Record<string, any> =
    hasOngoingCurrent && ongoingPayload
      ? ongoingPayload
      : (eventResultsQuery.data && Object.keys(eventResultsQuery.data).length > 0
          ? eventResultsQuery.data
          : {});

  const isCancelled = (effectiveEventResults as any)?.cancelled === true;

  const isSegmentBasedSport = Number(sportId) === BASEBALL_SPORT_ID || Number(sportId) === FOOTBALL_SPORT_ID;

  const rawVideoId =
    (effectiveEventResults as any)?.video_id ??
    (Array.isArray((effectiveEventResults as any)?.video_id_sequence) && (effectiveEventResults as any).video_id_sequence.length > 0
      ? (effectiveEventResults as any).video_id_sequence[0]
      : null);
  const miniTvVideoId = rawVideoId != null && String(rawVideoId).trim() !== '' ? String(rawVideoId).trim() : null;
  const videoIdSequence = Array.isArray((effectiveEventResults as any)?.video_id_sequence)
    ? (effectiveEventResults as any).video_id_sequence.map(String)
    : [];
  const useBatchVod = videoIdSequence.length > 0 && (Number(sportId) === BASEBALL_SPORT_ID || Number(sportId) === FOOTBALL_SPORT_ID);

  return {
    eventResultsQuery,
    effectiveEventResults,
    isCancelled,
    isSegmentBasedSport,
    miniTvVideoId,
    videoIdSequence,
    useBatchVod,
    pollExhausted,
  };
}
